import { TOKEN } from 'features/auth/constants';
import { getAuthToken, setAuthToken } from 'features/auth/helpers';
import { useGetCurrentUserQuery } from 'features/auth/query';
import { USE_SSO } from 'lib/constants';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { useAppDispatch } from 'lib/stores/hook';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import { TRoute } from 'routes/constants';
import { getPath } from 'routes/router-paths';

const PrivateRoute = ({
  children,
}: {
  children: JSX.Element;
  route?: TRoute;
}) => {
  const dispatch = useAppDispatch();
  const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);
  const auth = useAuth();
  const [isChecking, setIsChecking] = useState(true);

  const { data: currentUser, refetch: refetchCurrentUser } =
    useGetCurrentUserQuery(undefined, {
      skip: !accessToken,
    });

  useEffect(() => {
    async function initializeAuth() {
      if (!USE_SSO) {
        const storedToken = getAuthToken(TOKEN.ACCESS_TOKEN);
        if (storedToken && !accessToken) {
          dispatch(baseApi.util.resetApiState());
          setAuthToken(TOKEN.ACCESS_TOKEN, storedToken);
          await new Promise(resolve => setTimeout(resolve, 0));
          await refetchCurrentUser();
        }
      }
      setIsChecking(false);
    }

    void initializeAuth();
  }, [accessToken, dispatch, refetchCurrentUser]);

  useEffect(() => {
    async function syncSSOAuth() {
      if (USE_SSO && auth.user?.access_token) {
        setAuthToken(TOKEN.ACCESS_TOKEN, auth.user.access_token);
        await new Promise(resolve => setTimeout(resolve, 0));
        await refetchCurrentUser();
      }
      setIsChecking(false);
    }

    if (USE_SSO) {
      void syncSSOAuth();
    }
  }, [auth, dispatch, refetchCurrentUser]);

  if (isChecking || (USE_SSO && auth.isLoading)) {
    return <div>Loading...</div>;
  }

  const isAuthenticated = USE_SSO ? auth.isAuthenticated : !!accessToken;

  if (!isAuthenticated) {
    return <Navigate to={getPath('loginPage')} />;
  }

  return children;
};

export default PrivateRoute;
